import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Flex, Input } from 'ui';
import styled from 'styled-components';
import { injectIntl } from 'react-intl';
import { Button, Col, DatePicker, Row, Select, Tag, Tooltip } from 'antd';
import { originatorTagPrefix, ticketSnoozeOptions } from 'utils/app-configs';
import moment from 'utils/cops-moment';

const { Option } = Select;

const Label = styled.p`
  color: rgba(100,116,124,0.85);
  font-weight: 400;
  font-size: 13px;
  margin-top: 10px;
`;

const Description = styled.div`
  font-weight: 400;
  font-size: 16px;
`;

const Divider = styled.div`
  border-top: 1px solid rgba(100,116,124,0.4);
  height: 1px;
  margin: 15px 0;
`;

const StyledSelect = styled(Select)`
  font-size: 16px;
  width: 90%;
`;

const StyledDatePicker = styled(DatePicker)`
  input {
    font-weight: 400;
    font-size: 16px;
    height: 28px;
  }
  padding: 1px 10px;
  width: 100%;
`;

const Buttons = styled(Flex)`
  > :not(:last-child) {
    margin-right: 5px;
  }
  justify-content: flex-end;
  margin-top: 15px;
`;

export const StyledButton = styled(Button)`
  text-transform: uppercase;
  border: 2px solid #6f7e95 !important;
  color: ${p => p.primary ? 'white' : '#6f7e95'} !important;
  background-color: ${p => p.primary ? '#6f7e95' : 'white'} !important;
  &:hover {
    border: 2px solid #6f7e95 !important;
    color: ${p => p.primary ? 'white' : '#6f7e95'} !important;
    background-color: ${p => p.primary ? '#6f7e95' : 'white'} !important;
    opacity: 0.7 !important;
  }
  &:focus {
    border: 2px solid #6f7e95 !important;
    color: ${p => p.primary ? 'white' : '#6f7e95'} !important;
    background-color: ${p => p.primary ? '#6f7e95' : 'white'} !important;
  }
  &:disabled {
    opacity: 0.7;
  }
  ${p => p.marginTop && `margin-top: ${p.marginTop}px`};
`;

const SnoozeModal = ({ id, context, form, close, visible, isLoading, snoozeTicket, intl }) => {
  const [snoozeRange, setSnoozeRange] = useState(ticketSnoozeOptions?.find(o => o.isDefault).value);
  const [reason, setReason] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerValue, setDatePickerValue] = useState(moment().add(1, 'd'));
  const [snoozeDate, setSnoozeDate] = useState(moment().add(1, 'w'));

  const tag = [
    ...form.getFieldValue('classificationTags') || [],
    ...form.getFieldValue('conclusionTags') || [],
  ]?.find(t => originatorTagPrefix?.find(o => t.id.includes(o)));

  useEffect(() => {
    if (!visible) {
      setSnoozeRange(ticketSnoozeOptions?.find(o => o.isDefault).value);
      setReason('');
      setShowDatePicker(false);
      setDatePickerValue(moment().add(1, 'd'));
      setSnoozeDate(moment().add(1, 'w'));
    }
  }, [visible]);

  const handleRangeChange = (e) => {
    if (e === 'pick') {
      setShowDatePicker(true);
    } else {
      const range = ticketSnoozeOptions?.find(o => o.value === e);
      setShowDatePicker(false);
      setSnoozeDate(moment().add(range.amount, range.type));
    }
    setSnoozeRange(e);
  };

  const handleDateChange = (e) => {
    setDatePickerValue(e);
    setSnoozeDate(e);
  };

  const handleSnooze = () => {
    snoozeTicket({
      id,
      snoozeUntil: snoozeDate.utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
      comment: reason,
    });
  };

  const isInvalidDate = (current) => {
    const startDate = moment();
    const endDate = datePickerValue.clone().add(1, 'y');
    return current && (current < startDate.endOf('day') || current > endDate.endOf('day'));
  };

  const renderTag = () => {
    const tagDescription = `${tag.description} (${tag.id})`;
    const isLongTag = tagDescription.length > 20;
    const tagElem =
      <Tag
        key={tag.id}
        closable={false}
        color='blue'
      >
        {isLongTag ? `${tagDescription.slice(0, 20)}...` : tagDescription}
      </Tag>;
    return isLongTag ?
      <Tooltip title={tagDescription} key={tag.id}>
        {tagElem}
      </Tooltip>
      :
      tagElem;
  };

  const snoozeRangeOptions = ticketSnoozeOptions?.map(o => ({
    ...o,
    label: `${intl.formatMessage({
      id: `ticket.snoozeRanges.${o.labelKey}`,
    })}${o.value === 'pick' ? '...' : ''}`,
  }));

  return (
    <Flex ffcn>
      <Description>
        {intl.formatMessage({ id: 'ticket.SNOOZE_DESCRIPTION' })}:
      </Description>
      <Label>{intl.formatMessage({ id: `common.objects.${context?.type}` })}</Label>
      <Description>
        {/* eslint-disable-next-line */}
        {`${context?.line1} (${intl.formatMessage({ id: 'search.SERIAL_NUMBER_SHORT' })}: ${context?.line2})`}
      </Description>
      <Label>{intl.formatMessage({ id: 'ticket.TAG' })}</Label>
      <div>
        {renderTag()}
      </div>
      <Divider />
      <Row>
        <Col span={12}>
          <Label>{intl.formatMessage({ id: 'ticket.SNOOZE' })}</Label>
          <StyledSelect
            allowClear={false}
            value={snoozeRange}
            onSelect={handleRangeChange}
            disabled={isLoading}
          >
            {snoozeRangeOptions?.map(o => <Option key={o.id} value={o.value}>
              {o.label}
            </Option>)}
          </StyledSelect>
        </Col>
        <Col span={12}>
          {showDatePicker ?
            <>
              <Label>{intl.formatMessage({ id: 'ticket.DAY' })}</Label>
              <StyledDatePicker
                value={datePickerValue}
                format='L'
                disabledDate={isInvalidDate}
                showToday={false}
                onChange={handleDateChange}
                disabled={isLoading}
                allowClear={false}
              />
            </> :
            null
          }
        </Col>
      </Row>
      <Label>
        {/* eslint-disable-next-line */}
        {`${intl.formatMessage({ id: 'ticket.REASON' })} (${intl.formatMessage({ id: 'ticket.OPTIONAL' })})`}
      </Label>
      <div>
        <Input value={reason} onChange={e => setReason(e.target.value)} disabled={isLoading} />
      </div>
      <Buttons>
        <StyledButton
          onClick={close}
          disabled={isLoading}
        >
          {intl.formatMessage({ id: 'common.CANCEL' })}
        </StyledButton>
        <StyledButton
          primary={1}
          onClick={handleSnooze}
          disabled={isLoading}
          loading={isLoading}
        >
          {intl.formatMessage({ id: 'ticket.SNOOZE' })}
        </StyledButton>
      </Buttons>
    </Flex>
  );
};

SnoozeModal.propTypes = {
  id: PropTypes.string,
  context: PropTypes.object,
  form: PropTypes.object,
  close: PropTypes.func,
  visible: PropTypes.bool,
  isLoading: PropTypes.bool,
  snoozeTicket: PropTypes.func,
  intl: PropTypes.object,
};

export default injectIntl(SnoozeModal);
